<script>
import { Library } from "@observablehq/stdlib/";
import { D3Utility } from "../services/d3_utility.js";

export default {
  name: "SparkLines",
  props: {
    data: {
      required: true,
      type: Array,
    },
    chartData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      xScale: null,
      line: null,
      lines: null,
      bars: null,
      barWidth: null,
      yBarScale: null,
      yLineScale: null,
      gx: null,
      svg: null,
      xaxis: null,
      min: null,
      max: null,
      width: 700,
      height: 300,
      margin: 30,
      padding: 30,
      zoom: null,
      zoom_event: null,
      updated_data: null,
      clip: null,
      color: null,
      div: null,
      date_extent: null,
      computed_data: [],
      chart_config: null,
    };
  },
  watch: {
    data: function (newVal) {
      if (newVal && newVal.length > 0 && this.chartData) {
        if (this.svg) {
          this.svg.remove();
        }
        this.computed_data = this.data;
        this.init();
      }
    },
    chartData: function (newVal) {
      if (newVal && this.data) {
        if (this.svg) {
          this.svg.remove();
        }
        this.computed_data = this.data;
        this.init();
      }
    },
  },
  mounted() {
    if (this.data && this.data.length > 0 && this.chartData) {
      this.computed_data = this.data;
      this.init();
    }
  },
  methods: {
    init() {
      let colorScheme = this.chartData.colors;
      const width = D3Utility.get_conatiner_width(".container");
      this.width = width;
      const height = 300;
      const margin = 30;
      const padding = 30;
      this.chart_config = {
        width,
        height,
        legendHeight: 0,
        margin,
        padding,
      };

      this.updated_data = {
        line: [],
        bar: [],
      };
      this.computed_data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });

      var keys =
        this.updated_data["bar"].map((elem) => {
          return elem.id;
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push(elem.id);
      });
      let now = new Date();
      this.min_translate_x = 0;
      this.color = D3Utility.get_scale_ordinal(keys, colorScheme);
      this.xScale = D3Utility.get_utc_scale([margin, width - margin - padding]);
      this.yBarScale = D3Utility.get_linear_scale([
        height - margin - padding,
        margin,
      ]);
      this.yLineScale = D3Utility.get_linear_scale([
        height - margin - padding,
        margin,
      ]);
      this.date_extent = D3Utility.get_extent(this.computed_data[0].values);
      this.xScale.domain([
        new Date(this.date_extent[0]),
        new Date(this.date_extent[1]),
      ]);
      this.min = D3Utility.get_min(this.updated_data["line"]);
      this.max = D3Utility.get_max(this.updated_data["line"]);
      this.yLineScale.domain([this.min, this.max]).nice();
      this.yBarScale.domain([
        0,
        D3Utility.get_max(this.updated_data["bar"]) || 10,
      ]);

      //-----------------------------AXES-----------------------------//
      var dates = this.computed_data[0].values.map(function (v) {
        return v.key;
      });
      this.barWidth = D3Utility.get_scale_band(dates, [
        margin,
        width - margin - padding,
      ]);

      this.xaxis = (g, x) =>
        g
          .attr(
            "transform",
            `translate(${margin},${height - margin - padding})`,
          )
          .call(D3Utility.define_bottom_axis(x, width));
      this.div = D3Utility.append_tooltip();

      this.svg = D3Utility.create_svg(this.chartData.id, this.chart_config);

      const library = new Library();
      this.clip = library.DOM.uid("clip");

      this.svg = D3Utility.append_clip_path(
        this.chart_config,
        this.clip,
        this.svg,
      );

      //----------------------------LINES-----------------------------//

      this.line = D3Utility.get_line_obj(
        this.xScale,
        this.yLineScale,
        "natural",
      );

      //----------------------------LINES-----------------------------//
      this.addLinesAndBars();

      let new_date = new Date();
      new_date.setDate(new_date.getDate() - 1);

      let from_date = new Date();
      from_date.setDate(from_date.getDate() - parseInt(this.duration));

      this.max_translate_x =
        width - this.xScale(new Date(now)) - margin - margin;

      this.min_translate_x = -margin;
    },
    addLinesAndBars() {
      this.lines = D3Utility.append_lines(
        this.svg,
        this.updated_data["line"],
        this.chart_config,
        this.clip,
        this.line,
        this.color,
        this.xScale,
        this.yLineScale,
        this.div,
        this.chartData,
        true,
        this.min,
        this.max,
      );

      var keys =
        this.updated_data["bar"].map((elem) => {
          return {
            id: elem.id,
            label: elem.label,
          };
        }) || [];

      this.updated_data["line"].map((elem) => {
        keys.push({
          id: elem.id,
          label: elem.label,
        });
      });
    },
  },
  computed: {},
};
</script>

<template>
  <div id="sparklines-container" class="container pa-0">
    <div :id="`${chartData.id}`" class="svg-container"></div>
  </div>
</template>

<style lang="scss">
/* LINE CHART */
.serie_label {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 80%;
}
.pointer {
  cursor: pointer;
}
.caption {
  color: var(--v-primaryGrey-base);
}
.shadow,
path:hover {
  -webkit-filter: drop-shadow(6px 4px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(6px 4px 2px rgba(0, 0, 0, 0.7));
}
rect:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}
circle:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}
.hide {
  display: none;
}
#sparklines-container path[id^="line-"] {
  stroke-width: 6;
}
.text {
  color: black;
}
</style>
